import { v4 as uuid } from 'uuid';

import {
  AFFECTED_OBJECT_DECISION,
  AFFECTED_OBJECT_SUGGESTION,
  ASSESSMENT_VALUE_SOURCE,
  CONFIRMATION_STATE,
  PREFILL_KIND,
  SECTION_STATUS,
  SYSTEM_CHANGE_FIELD_KEY,
  TAffectedObjectDecision,
  TAffectedObjectState,
  TApproverRole,
  TAssessmentFieldValue,
  TAssessmentQuestionConfig,
  TAssessmentSectionConfig,
  TAssessmentSubmission,
  TAssessmentTemplateConfig,
  TAssessmentValueSource,
  TChangeAssessment,
  TChangeAssessmentTemplateSnapshot,
  TChangeV2,
  TCandidateAffectedObjectConfig,
  TDraftPlanningItem,
  TFieldValue,
  TPrefillKind,
  TTemplateCustomFieldConfig,
  TTemplateInputValue,
  TEMPLATE_FIELD_TYPE,
  TTemplateFieldType,
} from '../models';
import { PLATFORM_CONDITIONAL_SECTIONS } from '../data/assessmentTemplates';
import { evaluateCondition, TValueResolver } from './conditions';
import { resolveTokensFromValues } from './resolveTemplateString';
import { makeAssessmentSectionOptional, simplifyAssessmentTemplate } from './simplifyAssessmentTemplate';

// -- Value helpers ----------------------------------------------------------

const emptyValueFor = (type: TTemplateFieldType): TFieldValue =>
  type === TEMPLATE_FIELD_TYPE.MULTI_SELECT ? [] : null;

export const isValueAnswered = (value: TFieldValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const sourceForPrefill = (kind: TPrefillKind): TAssessmentValueSource => {
  switch (kind) {
    case PREFILL_KIND.REQUEST_FACT:
      return ASSESSMENT_VALUE_SOURCE.REQUEST;
    case PREFILL_KIND.TEMPLATE_DEFAULT:
      return ASSESSMENT_VALUE_SOURCE.TEMPLATE_DEFAULT;
    case PREFILL_KIND.TEMPLATE_SUGGESTION:
      return ASSESSMENT_VALUE_SOURCE.TEMPLATE_SUGGESTION;
    default:
      return ASSESSMENT_VALUE_SOURCE.ASSESSOR;
  }
};

const confirmationForPrefill = (kind: TPrefillKind) =>
  kind === PREFILL_KIND.REQUEST_FACT ? CONFIRMATION_STATE.CONFIRMED_FROM_REQUEST : CONFIRMATION_STATE.UNCONFIRMED;

const initialCustomFieldValue = (field: TTemplateCustomFieldConfig, at: string): TAssessmentFieldValue => ({
  fieldId: field.id,
  fieldOrigin: 'template',
  value: field.prefilledValue ?? field.defaultValue ?? emptyValueFor(field.fieldType),
  source: sourceForPrefill(field.prefillKind),
  confirmationState: confirmationForPrefill(field.prefillKind),
  lastUpdatedBy: 'Template',
  lastUpdatedAt: at,
});

const initialAnswerValue = (question: TAssessmentQuestionConfig, at: string): TAssessmentFieldValue => ({
  fieldId: question.id,
  fieldOrigin: 'template',
  value: question.prefilledValue ?? question.defaultValue ?? emptyValueFor(question.controlType),
  source: sourceForPrefill(question.prefillKind),
  confirmationState: confirmationForPrefill(question.prefillKind),
  lastUpdatedBy: 'Template',
  lastUpdatedAt: at,
  comment: question.prefilledComment,
});

// -- Instantiation ----------------------------------------------------------

// Builds the immutable snapshot: the template's sections plus the platform-level
// conditional sections. Later template edits never touch this.
export const buildTemplateSnapshot = (
  template: TAssessmentTemplateConfig,
  instantiatedAt: string
): TChangeAssessmentTemplateSnapshot => {
  const demoTemplate = simplifyAssessmentTemplate(template);
  return {
    templateId: template.id,
    templateVersion: template.version,
    instantiatedAt,
    customFields: demoTemplate.customFields,
    assessmentSections: [
      ...demoTemplate.assessmentSections,
      ...PLATFORM_CONDITIONAL_SECTIONS.map(makeAssessmentSectionOptional),
    ],
    candidateAffectedObjects: demoTemplate.candidateAffectedObjects,
    preliminaryActivities: demoTemplate.preliminaryActivities,
  };
};

// Resolves `{{input:...}}` tokens in the user-facing strings of a snapshot using a
// Change's stored template input values. Only labels, help text, titles,
// descriptions, string prefills and activity titles are resolved — never ids,
// enum options, conditions or other technical keys.
const resolveSnapshotTokens = (
  snapshot: TChangeAssessmentTemplateSnapshot,
  inputValues: Record<string, TTemplateInputValue>
): TChangeAssessmentTemplateSnapshot => {
  const text = (value: string) => resolveTokensFromValues(value, inputValues);
  const optionalText = (value?: string) => (value ? text(value) : value);
  const stringValue = (value: TFieldValue | undefined) => (typeof value === 'string' ? text(value) : value);
  return {
    ...snapshot,
    customFields: snapshot.customFields.map((field) => ({
      ...field,
      label: text(field.label),
      helpText: optionalText(field.helpText),
      prefilledValue: stringValue(field.prefilledValue),
    })),
    assessmentSections: snapshot.assessmentSections.map((section) => ({
      ...section,
      title: text(section.title),
      description: text(section.description),
      questions: section.questions.map((question) => ({
        ...question,
        label: text(question.label),
        helpText: optionalText(question.helpText),
        prefilledValue: stringValue(question.prefilledValue),
        prefilledComment: optionalText(question.prefilledComment),
      })),
    })),
    candidateAffectedObjects: snapshot.candidateAffectedObjects.map((candidate) => ({
      ...candidate,
      label: text(candidate.label),
      documents: candidate.documents?.map((document) => ({ ...document, name: text(document.name) })),
    })),
    preliminaryActivities: snapshot.preliminaryActivities.map((activity) => ({
      ...activity,
      title: text(activity.title),
      description: text(activity.description),
      completionCriteria: text(activity.completionCriteria),
      linkedObject: activity.linkedObject ? text(activity.linkedObject) : undefined,
      documents: activity.documents?.map((document) => ({
        ...document,
        name: text(document.name),
      })),
    })),
  };
};

// Instantiates a fresh assessment record from a template config. All template
// sections plus platform sections are represented; values are seeded from
// prefills/defaults; nothing is marked complete. `inputValues` resolves any
// `{{input:...}}` tokens the template carries against the Change's stored facts.
export const instantiateAssessment = (
  template: TAssessmentTemplateConfig,
  instantiatedAt: string,
  inputValues: Record<string, TTemplateInputValue> = {}
): TChangeAssessment => {
  const snapshot = resolveSnapshotTokens(buildTemplateSnapshot(template, instantiatedAt), inputValues);

  const customFieldValues: Record<string, TAssessmentFieldValue> = {};
  snapshot.customFields.forEach((field) => {
    customFieldValues[field.id] = initialCustomFieldValue(field, instantiatedAt);
  });

  const answers: Record<string, TAssessmentFieldValue> = {};
  const sectionProgress: TChangeAssessment['sectionProgress'] = {};
  snapshot.assessmentSections.forEach((section) => {
    sectionProgress[section.id] = { sectionId: section.id, status: SECTION_STATUS.NOT_STARTED };
    section.questions.forEach((question) => {
      answers[question.id] = initialAnswerValue(question, instantiatedAt);
    });
  });

  const affectedObjects: TAffectedObjectState[] = snapshot.candidateAffectedObjects.map((candidate) => ({
    objectId: candidate.id,
    decision: AFFECTED_OBJECT_DECISION.NOT_ASSESSED,
    responsibleFunction: candidate.responsibleFunction,
  }));

  return {
    templateId: template.id,
    templateVersion: template.version,
    snapshot,
    customFieldValues,
    answers,
    sectionProgress,
    affectedObjects,
    hiddenValues: {},
  };
};

// -- Value resolution & visibility -----------------------------------------

// Maps a platform field key to the Change's live value so conditions and the
// assessment summary can read platform facts alongside answers.
const resolvePlatformField = (change: TChangeV2, key: string): TFieldValue | undefined => {
  switch (key) {
    case SYSTEM_CHANGE_FIELD_KEY.KNOWN_SAFETY_CONCERN:
      return change.knownSafetyConcern ?? null;
    case SYSTEM_CHANGE_FIELD_KEY.CHANGE_CATEGORY:
      return change.changeCategory ?? null;
    case SYSTEM_CHANGE_FIELD_KEY.SCOPE:
      return change.scopeType ?? null;
    case SYSTEM_CHANGE_FIELD_KEY.CRITICALITY:
      return change.criticality ?? null;
    case SYSTEM_CHANGE_FIELD_KEY.URGENCY:
      return change.urgency ?? null;
    default:
      return undefined;
  }
};

// A resolver that reads answers first, then custom field values, then platform
// facts - so a condition can reference any of the three field layers by key.
export const makeValueResolver =
  (assessment: TChangeAssessment, change: TChangeV2): TValueResolver =>
  (key) => {
    if (assessment.answers[key]) return assessment.answers[key].value;
    if (assessment.customFieldValues[key]) return assessment.customFieldValues[key].value;
    return resolvePlatformField(change, key);
  };

// The immediate-containment platform section is visible only when the request created
// containment work; other sections use their configured visibleWhen condition.
export const isSectionVisible = (
  section: TAssessmentSectionConfig,
  change: TChangeV2,
  resolve: TValueResolver
): boolean => {
  if (section.platformSection === 'immediateContainment') return change.immediateContainment.required;
  return evaluateCondition(section.visibleWhen, resolve);
};

export const isCustomFieldVisible = (field: TTemplateCustomFieldConfig, resolve: TValueResolver): boolean =>
  evaluateCondition(field.visibleWhen, resolve);

export const isCustomFieldRequired = (field: TTemplateCustomFieldConfig, resolve: TValueResolver): boolean =>
  field.required || (field.requiredWhen ? evaluateCondition(field.requiredWhen, resolve) : false);

export const isQuestionVisible = (question: TAssessmentQuestionConfig, resolve: TValueResolver): boolean =>
  evaluateCondition(question.visibleWhen, resolve);

export const isQuestionRequired = (question: TAssessmentQuestionConfig, resolve: TValueResolver): boolean =>
  question.required || (question.requiredWhen ? evaluateCondition(question.requiredWhen, resolve) : false);

export const isRationaleRequired = (question: TAssessmentQuestionConfig, resolve: TValueResolver): boolean =>
  question.rationaleRequiredWhen ? evaluateCondition(question.rationaleRequiredWhen, resolve) : false;

// The sections visible for a Change, template sections first then platform ones.
export const getVisibleSections = (assessment: TChangeAssessment, change: TChangeV2): TAssessmentSectionConfig[] => {
  const resolve = makeValueResolver(assessment, change);
  return assessment.snapshot.assessmentSections.filter((section) => isSectionVisible(section, change, resolve));
};

// -- Completion & progress --------------------------------------------------

export type TSectionCompletion = {
  complete: boolean;
  requiredTotal: number;
  requiredAnswered: number;
  missing: string[];
};

// A section is completable when all required visible questions are answered and
// their required rationales are present. Custom fields count too when required.
export const evaluateSectionCompletion = (
  section: TAssessmentSectionConfig,
  assessment: TChangeAssessment,
  change: TChangeV2,
  customFields: TTemplateCustomFieldConfig[]
): TSectionCompletion => {
  const resolve = makeValueResolver(assessment, change);
  const missing: string[] = [];
  let requiredTotal = 0;
  let requiredAnswered = 0;

  section.questions.forEach((question) => {
    if (!isQuestionVisible(question, resolve)) return;
    if (isQuestionRequired(question, resolve)) {
      requiredTotal += 1;
      const answer = assessment.answers[question.id];
      const answered = isValueAnswered(answer?.value);
      const rationaleOk = !isRationaleRequired(question, resolve) || Boolean(answer?.rationale?.trim());
      if (answered && rationaleOk) requiredAnswered += 1;
      else missing.push(question.label);
    }
  });

  section.customFieldIds.forEach((fieldId) => {
    const field = customFields.find((candidate) => candidate.id === fieldId);
    if (!field || !isCustomFieldVisible(field, resolve)) return;
    if (isCustomFieldRequired(field, resolve)) {
      requiredTotal += 1;
      if (isValueAnswered(assessment.customFieldValues[fieldId]?.value)) requiredAnswered += 1;
      else missing.push(field.label);
    }
  });

  return { complete: missing.length === 0, requiredTotal, requiredAnswered, missing };
};

// Number of required, visible questions still unanswered across visible sections.
export const countOpenQuestions = (assessment: TChangeAssessment, change: TChangeV2): number => {
  const customFields = assessment.snapshot.customFields;
  return getVisibleSections(assessment, change).reduce((total, section) => {
    const completion = evaluateSectionCompletion(section, assessment, change, customFields);
    return total + (completion.requiredTotal - completion.requiredAnswered);
  }, 0);
};

export const countCompleteSections = (assessment: TChangeAssessment, change: TChangeV2): number =>
  getVisibleSections(assessment, change).filter(
    (section) => assessment.sectionProgress[section.id]?.status === SECTION_STATUS.COMPLETE
  ).length;

// All required, visible sections are complete - the gate for submitting.
export const areAllRequiredSectionsComplete = (assessment: TChangeAssessment, change: TChangeV2): boolean => {
  const visible = getVisibleSections(assessment, change);
  return (
    visible.length > 0 &&
    visible.every((section) => assessment.sectionProgress[section.id]?.status === SECTION_STATUS.COMPLETE)
  );
};

export const countConfirmedAffectedObjects = (assessment: TChangeAssessment): number =>
  assessment.affectedObjects.filter((object) => object.decision === AFFECTED_OBJECT_DECISION.AFFECTED).length;

// Rationales are optional for affected-object decisions, except when an assessor
// overrides a likely-affected candidate by deciding that it is not affected.
export const isAffectedObjectRationaleRequired = (
  candidate: TCandidateAffectedObjectConfig,
  decision: TAffectedObjectDecision
): boolean =>
  candidate.initialSuggestion === AFFECTED_OBJECT_SUGGESTION.LIKELY_AFFECTED &&
  decision === AFFECTED_OBJECT_DECISION.NOT_AFFECTED;

export const areAffectedObjectRationalesComplete = (assessment: TChangeAssessment): boolean =>
  assessment.snapshot.candidateAffectedObjects.every((candidate) => {
    const state = assessment.affectedObjects.find((object) => object.objectId === candidate.id);
    return !state || !isAffectedObjectRationaleRequired(candidate, state.decision) || Boolean(state.rationale?.trim());
  });

// -- Approval route ---------------------------------------------------------

// Returns the configured signing functions (never individuals) and why each is
// relevant to this supplier and patient-contacting component assessment.
export const computeApproverRoles = (_assessment: TChangeAssessment, _change: TChangeV2): TApproverRole[] => {
  const roles: TApproverRole[] = [];
  roles.push(
    { role: 'Supplier quality', reason: 'Supplier qualification and quality agreement' },
    { role: 'Engineering or R&D', reason: 'Component equivalence and product impact' },
    { role: 'Quality assurance', reason: 'Change oversight, risk and controlled implementation' },
    { role: 'Regulatory affairs', reason: 'Regulatory, UDI and notified-body impact' },
    { role: 'Biological safety', reason: 'Patient-contacting adhesive impact' },
    { role: 'Production or operations', reason: 'Incoming inspection and production cutover' }
  );

  return roles;
};

export const buildSubmissionPreview = (
  assessment: TChangeAssessment,
  change: TChangeV2,
  actor: string,
  submittedAt: string,
  version: number
): TAssessmentSubmission => ({
  version,
  submittedBy: actor,
  submittedAt,
  completedSections: countCompleteSections(assessment, change),
  confirmedAffectedObjects: countConfirmedAffectedObjects(assessment),
  unresolvedNonBlockingQuestions: countOpenQuestions(assessment, change),
  approverRoles: computeApproverRoles(assessment, change),
});

// -- Draft planning items (after assessment approval) -----------------------

export const generateDraftPlanningItems = (assessment: TChangeAssessment): TDraftPlanningItem[] =>
  assessment.snapshot.preliminaryActivities.map((activity) => ({
    id: `dpi-${uuid()}`,
    title: activity.title,
    responsibleFunction: activity.responsibleFunction,
    owner: activity.owner,
    dueInDays: activity.dueInDays,
    status: 'Draft',
  }));
